import { useCallback, useEffect, useRef, useState } from 'react'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'

import appConfig from '~/config/app'

import { giveAdditional2CorrectConsecutiveAnswersActionCreator } from '~/bootcamp/slices/bootcamp-activity.slice'

import { useAppDispatch } from '~/redux/hooks'

import {
  CircleCombinationsMessage,
  CircleCombinationsQuestionMessageData,
} from '~/bootcamp/Activities/types/activities.types'

import { CircleCombinationsActivityData, CircleCombinationsProps } from './CircleCombinations.type'
import { generateCircleCombinationsActivityData } from './CircleCombinations.util'

const emptyQuestion = {
  question: [],
  questionId: '',
  maximumRightAnswer: 1,
  shouldShuffle: false,
  _key: '',
  circleElements: [],
  randomizeCircleElementsOrder: false,
  validCircleCombinations: [],
  combinationsToFind: 3,
}

function CircleCombinations({
  answerState,
  onNextQuestionClick,
  onAnswerStateChange,
  actionButtonsFC,
  questions,
}: CircleCombinationsProps): React.ReactElement {
  const [answer, setAnswer] = useState<boolean[]>([])
  const [questionHistory, setQuestionHistory] = useState<number[]>([])
  const [activityData, setActivityData] = useState<CircleCombinationsActivityData>({
    question: emptyQuestion,
    questionIndex: 0,
  })
  const [isLoaded, setIsLoaded] = useState(false)
  const circleCombinationsFrameRef = useRef<HTMLIFrameElement>(null)
  const circleCombinationsUrl = appConfig.playcanvas.circleCombiantions
  const dispatch = useAppDispatch()

  useEffect(() => {
    setAnswer([])
    setActivityData(generateCircleCombinationsActivityData(questions, questionHistory))
  }, [questionHistory, questions])

  useEffect(() => {
    onAnswerStateChange(null)
  }, [answer, onAnswerStateChange])

  const messageCircleCombinationsActivity = useCallback(
    (message: CircleCombinationsMessage): void => {
      const postMessage = circleCombinationsFrameRef.current?.contentWindow
      if (postMessage) {
        postMessage.postMessage(message, circleCombinationsUrl.origin)
      }
    },
    [circleCombinationsUrl.origin],
  )

  const messageHandler = useCallback(
    (event: { origin: string; data: { state: string } }) => {
      // check that the message is coming from playcanvas
      if (event.origin === circleCombinationsUrl.origin) {
        const messageData = event.data

        if (messageData.state === 'ready') {
          setIsLoaded(true)
        }

        if (messageData.state === 'circleCombinationsActivityIsCompleted') {
          // handle victory
          dispatch(giveAdditional2CorrectConsecutiveAnswersActionCreator())
          onAnswerStateChange(true, activityData.question.questionId, true)
        }
      }
    },
    [circleCombinationsUrl.origin, activityData.question.questionId, onAnswerStateChange, dispatch],
  )

  useEffect(() => {
    if (isLoaded) {
      const { circleElements, randomizeCircleElementsOrder, validCircleCombinations, combinationsToFind } =
        activityData.question
      const questionMessageData: CircleCombinationsQuestionMessageData = {
        circleElements,
        randomizeCircleElementsOrder,
        validCircleCombinations,
        combinationsToFind,
      }
      messageCircleCombinationsActivity({
        questionMessageData,
      })
    }
  }, [isLoaded, messageCircleCombinationsActivity, activityData])

  useEffect(() => {
    window.addEventListener('message', messageHandler)
    if (!isLoaded) messageCircleCombinationsActivity({ type: 'ready' })

    return () => window.removeEventListener('message', messageHandler)
  }, [messageHandler, messageCircleCombinationsActivity, isLoaded])

  const handleOnNextQuestionClick = (): void => {
    setQuestionHistory((prevState) => {
      return [activityData.questionIndex, ...prevState].slice(0, questions.length - 1)
    })
    onAnswerStateChange(null)
    onNextQuestionClick()
  }

  return (
    <ActivityWrapper>
      <div className="my-auto py-8 md:py-0 w-full flex-col flex-wrap justify-center items-center text-purple font-semibold text-md">
        <iframe
          className="select-none w-full rounded-b -z-1"
          ref={circleCombinationsFrameRef}
          src={circleCombinationsUrl.href}
          style={{
            minHeight: 'min(35rem, calc(100vw * 0.82))', // Ensures aspect ratio is not too small for playcanvas
          }}
        />
      </div>

      {actionButtonsFC &&
        actionButtonsFC({
          formValid: answer.filter((a) => a).length > 0,
          correctAnswer: answerState ?? false,
          submitAnswerAction: () => {},
          clearFormAction: () => {
            setAnswer([])
          },
          goToNextQuestionAction: handleOnNextQuestionClick,
        })}
    </ActivityWrapper>
  )
}

export default CircleCombinations
